import set from "lodash/set";

export const LOAN_FETCHED_DATA_TYPES = {
  LOAN_TERMS: "loanTerms",
  FUNDING_ENTITIES: "fundingEntities",
  UW_REQUIREMENTS: "uwRequirements",
  ESCROW_ITEMS: "escrowItems",
  PARSED_DOCS: "parsedDocs",
  EMAIL_TEMPLATES: "emailTemplates",
  SIZER_UPLOADS: "sizerUploads",
  LIQUIDITY_SOURCES: "liquiditySources",
  // DscrCalc for the deal — sources[] plus the server-computed stated/
  // validated/verified DSCR. DSCR-product loans only.
  DSCR_CALC: "dscrCalc",
  // The `componentType` catalog behind the DSCR workbook's Add-Source drawer
  // (`GET /pricing/dscr_calc_components` → `{componentType, bucket, annual}`).
  // Held here so the tab and the sidebar read ONE fetched copy — the sidebar
  // filters it by `bucket` (income vs expense).
  DSCR_CALC_COMPONENTS: "dscrCalcComponents",
  // Extract candidates behind the DSCR drawers' evidence pickers
  // (`GET /pricing/dscr_calc_evidence/{dealId}`). Cached per deal so opening
  // a drawer doesn't refetch; cleared with the calc on refresh.
  DSCR_CALC_EVIDENCE: "dscrCalcEvidence",
  REQUIRED_LIQUIDITY: "requiredLiquidity",
  // Document audit log for the loan — sequential record of doc uploads,
  // status changes, and accept/reject events. Used by Key Dates &
  // Documents to derive a sequential timeline (first-upload date, accepted
  // date, etc.) and by anything else that needs the chronological view of
  // document state changes.
  AUDIT_LOGS: "auditLogs",
  // Borrower Experience Workbook for the deal (`GET /experience/check`):
  // `{ items[], experienceApplies, experienceChatEnabled, totalVerifiedExperience }`.
  // One cached copy shared by every surface that mounts the workbench.
  EXPERIENCE: "experience",
};

const initState = {
  [LOAN_FETCHED_DATA_TYPES.LOAN_TERMS]: null,
  [LOAN_FETCHED_DATA_TYPES.FUNDING_ENTITIES]: null,
  [LOAN_FETCHED_DATA_TYPES.UW_REQUIREMENTS]: null,
  [LOAN_FETCHED_DATA_TYPES.ESCROW_ITEMS]: null,
  [LOAN_FETCHED_DATA_TYPES.PARSED_DOCS]: null,
  [LOAN_FETCHED_DATA_TYPES.EMAIL_TEMPLATES]: null,
  [LOAN_FETCHED_DATA_TYPES.SIZER_UPLOADS]: null,
  [LOAN_FETCHED_DATA_TYPES.LIQUIDITY_SOURCES]: null,
  [LOAN_FETCHED_DATA_TYPES.DSCR_CALC]: null,
  [LOAN_FETCHED_DATA_TYPES.DSCR_CALC_COMPONENTS]: null,
  [LOAN_FETCHED_DATA_TYPES.DSCR_CALC_EVIDENCE]: null,
  [LOAN_FETCHED_DATA_TYPES.REQUIRED_LIQUIDITY]: null,
  [LOAN_FETCHED_DATA_TYPES.AUDIT_LOGS]: null,
  [LOAN_FETCHED_DATA_TYPES.EXPERIENCE]: null,
};

const loanFetchedDataReducer = (state = initState, action) => {
  let newState = { ...state };

  switch (action.type) {
    case "SET_LOAN_FETCHED_DATA_KEY":
      return set(newState, action.payload.key, action.payload.value);

    case "SET_LOAN_FETCHED_DATA_VALUES":
      return {
        ...newState,
        ...action.payload,
      };

    case "SET_LOAN_FETCHED_DATA_RESOURCE": {
      const { resourceKey, data } = action.payload;
      newState[resourceKey] = data;
      return newState;
    }

    case "CLEAR_LOAN_FETCHED_DATA_RESOURCE": {
      const resourceKey = action.payload;

      if (resourceKey in initState) {
        newState[resourceKey] = initState[resourceKey];
      }

      return newState;
    }

    case "CLEAR_LOAN_FETCHED_DATA":
      return initState;

    default:
      return state;
  }
};

export default loanFetchedDataReducer;

// ------------------- action creators -------------------

// generic
export const loanFetchedDataSetKey = (key, value) => ({
  type: "SET_LOAN_FETCHED_DATA_KEY",
  payload: { key, value },
});

export const loanFetchedDataSetValues = (values) => ({
  type: "SET_LOAN_FETCHED_DATA_VALUES",
  payload: values,
});

// fetched-resource convenience
export const loanFetchedDataSetResource = (resourceKey, data) => ({
  type: "SET_LOAN_FETCHED_DATA_RESOURCE",
  payload: { resourceKey, data },
});

export const loanFetchedDataClearResource = (resourceKey) => ({
  type: "CLEAR_LOAN_FETCHED_DATA_RESOURCE",
  payload: resourceKey,
});

// clears
export const loanFetchedDataClearAll = () => ({
  type: "CLEAR_LOAN_FETCHED_DATA",
});

// ###### ###### ######
//  ENDPOINT SPECIFIC
// ###### ###### ######

// Loan Terms
export const loanFetchedDataLoanTermsSet = (data) => ({
  type: "SET_LOAN_FETCHED_DATA_RESOURCE",
  payload: { resourceKey: LOAN_FETCHED_DATA_TYPES.LOAN_TERMS, data },
});

export const loanFetchedDataLoanTermsClear = () => ({
  type: "CLEAR_LOAN_FETCHED_DATA_RESOURCE",
  payload: { resourceKey: LOAN_FETCHED_DATA_TYPES.LOAN_TERMS },
});

// Loan Funding Entities
export const loanFetchedDataFundingEntitiesSet = (data) => ({
  type: "SET_LOAN_FETCHED_DATA_RESOURCE",
  payload: { resourceKey: LOAN_FETCHED_DATA_TYPES.FUNDING_ENTITIES, data },
});

export const loanFetchedDataFundingEntitiesClear = () => ({
  type: "CLEAR_LOAN_FETCHED_DATA_RESOURCE",
  payload: { resourceKey: LOAN_FETCHED_DATA_TYPES.FUNDING_ENTITIES },
});

// Loan Escrow Items
export const loanFetchedDataEscrowItemsSet = (data) => ({
  type: "SET_LOAN_FETCHED_DATA_RESOURCE",
  payload: { resourceKey: LOAN_FETCHED_DATA_TYPES.ESCROW_ITEMS, data },
});

export const loanFetchedDataEscrowItemsClear = () => ({
  type: "CLEAR_LOAN_FETCHED_DATA_RESOURCE",
  payload: { resourceKey: LOAN_FETCHED_DATA_TYPES.ESCROW_ITEMS },
});

// Loan Escrow Items
export const loanFetchedDataParsedDocsSet = (data) => ({
  type: "SET_LOAN_FETCHED_DATA_RESOURCE",
  payload: { resourceKey: LOAN_FETCHED_DATA_TYPES.PARSED_DOCS, data },
});

export const loanFetchedDataParsedDocsClear = () => ({
  type: "CLEAR_LOAN_FETCHED_DATA_RESOURCE",
  payload: { resourceKey: LOAN_FETCHED_DATA_TYPES.PARSED_DOCS },
});

// UW_REQUIREMENTS
export const loanFetchedDataUwRequirementsSet = (data) => ({
  type: "SET_LOAN_FETCHED_DATA_RESOURCE",
  payload: { resourceKey: LOAN_FETCHED_DATA_TYPES.UW_REQUIREMENTS, data },
});

export const loanFetchedDataUwRequirementsClear = () => ({
  type: "CLEAR_LOAN_FETCHED_DATA_RESOURCE",
  payload: { resourceKey: LOAN_FETCHED_DATA_TYPES.UW_REQUIREMENTS },
});

// SIZER UPLOADS
export const loanFetchedDataSizerUploadsSet = (data) => ({
  type: "SET_LOAN_FETCHED_DATA_RESOURCE",
  payload: { resourceKey: LOAN_FETCHED_DATA_TYPES.SIZER_UPLOADS, data },
});

export const loanFetchedDataSizerUploadsClear = () => ({
  type: "CLEAR_LOAN_FETCHED_DATA_RESOURCE",
  payload: LOAN_FETCHED_DATA_TYPES.SIZER_UPLOADS,
});

// EMAIL TEMPLATES
export const loanFetchedDataEmailTemplatesSet = (data) => ({
  type: "SET_LOAN_FETCHED_DATA_RESOURCE",
  payload: { resourceKey: LOAN_FETCHED_DATA_TYPES.EMAIL_TEMPLATES, data },
});

export const loanFetchedDataEmailTemplatesClear = () => ({
  type: "CLEAR_LOAN_FETCHED_DATA_RESOURCE",
  payload: { resourceKey: LOAN_FETCHED_DATA_TYPES.EMAIL_TEMPLATES },
});

// DSCR CALC
export const loanFetchedDataDscrCalcSet = (data) => ({
  type: "SET_LOAN_FETCHED_DATA_RESOURCE",
  payload: { resourceKey: LOAN_FETCHED_DATA_TYPES.DSCR_CALC, data },
});

export const loanFetchedDataDscrCalcComponentsSet = (data) => ({
  type: "SET_LOAN_FETCHED_DATA_RESOURCE",
  payload: { resourceKey: LOAN_FETCHED_DATA_TYPES.DSCR_CALC_COMPONENTS, data },
});

export const loanFetchedDataDscrCalcEvidenceSet = (data) => ({
  type: "SET_LOAN_FETCHED_DATA_RESOURCE",
  payload: { resourceKey: LOAN_FETCHED_DATA_TYPES.DSCR_CALC_EVIDENCE, data },
});

// LIQUIDITY SOURCES

export const loanFetchedDataLiquiditySourcesSet = (data) => ({
  type: "SET_LOAN_FETCHED_DATA_RESOURCE",
  payload: { resourceKey: LOAN_FETCHED_DATA_TYPES.LIQUIDITY_SOURCES, data },
});

export const loanFetchedDataLiquiditySourcesClear = () => ({
  type: "CLEAR_LOAN_FETCHED_DATA_RESOURCE",
  payload: LOAN_FETCHED_DATA_TYPES.LIQUIDITY_SOURCES,
});

// EXPERIENCE WORKBOOK
export const loanFetchedDataExperienceSet = (data) => ({
  type: "SET_LOAN_FETCHED_DATA_RESOURCE",
  payload: { resourceKey: LOAN_FETCHED_DATA_TYPES.EXPERIENCE, data },
});

// REQUIRED LIQUIDITY
export const loanFetchedDataRequiredLiquiditySet = (data) => ({
  type: "SET_LOAN_FETCHED_DATA_RESOURCE",
  payload: { resourceKey: LOAN_FETCHED_DATA_TYPES.REQUIRED_LIQUIDITY, data },
});

export const loanFetchedDataRequiredLiquidityClear = () => ({
  type: "CLEAR_LOAN_FETCHED_DATA_RESOURCE",
  payload: LOAN_FETCHED_DATA_TYPES.REQUIRED_LIQUIDITY,
});

// AUDIT LOGS
export const loanFetchedDataAuditLogsSet = (data) => ({
  type: "SET_LOAN_FETCHED_DATA_RESOURCE",
  payload: { resourceKey: LOAN_FETCHED_DATA_TYPES.AUDIT_LOGS, data },
});

export const loanFetchedDataAuditLogsClear = () => ({
  type: "CLEAR_LOAN_FETCHED_DATA_RESOURCE",
  payload: LOAN_FETCHED_DATA_TYPES.AUDIT_LOGS,
});
